import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Edit, FileSignature, Building, Calendar, Users, TrendingUp, History, Save, AlertCircle, Download } from "lucide-react";

const QUARTERS = [];
for (let year = 2020; year <= new Date().getFullYear() + 1; year++) {
  for (let q = 1; q <= 4; q++) QUARTERS.push(`${year}-T${q}`);
}

const empty = {
  entity_id: "", property_id: "", project_id: "",
  tenant_name: "", tenant_siret: "", tenant_contact: "",
  property_label: "",
  start_date: new Date().toISOString().slice(0, 10), end_date: "", duration_months: 36,
  monthly_rent_ht: 0, monthly_charges: 0, tva_rate: 20,
  taxe_fonciere_annual: 0, taxe_fonciere_refacturee: false,
  deposit: 0, indexation_index: "ILC", indexation_rate_pct: 0,
  // Indexation INSEE
  initial_rent_ht: "",
  indexation_reference_index: "",
  indexation_reference_quarter: "",
  indexation_next_review_date: "",
  status: "actif", notes: "",
  payment_day: 1,
  current_month_collected: false,
  current_month_collected_at: "",
};

const STATUS_LABEL = {
  actif: "Actif",
  expire: "Expiré",
  rompu: "Rompu",
  en_negociation: "En négociation",
};
const STATUS_TONE = {
  actif: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  expire: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  rompu: "bg-red-500/10 text-red-300 border-red-500/30",
  en_negociation: "bg-sky-500/10 text-sky-300 border-sky-500/30",
};

export default function LeasesAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [existingProps, setExistingProps] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importCandidates, setImportCandidates] = useState([]);
  const [importSelection, setImportSelection] = useState({});
  const [importEntityId, setImportEntityId] = useState("");
  const [importBusy, setImportBusy] = useState(false);

  const load = () =>
    Promise.all([
      api.get(`/backoffice/leases${filterStatus ? `?status=${filterStatus}` : ""}`).then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
      api.get("/backoffice/projects").then((r) => r.data),
      api.get("/backoffice/existing-properties").then((r) => r.data).catch(() => []),
    ]).then(([l, e, p, ep]) => {
      setItems(l); setEntities(e); setProjects(p); setExistingProps(ep);
    });
  useEffect(() => { load(); }, [filterStatus]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, entity_id: entities[0]?.id || "" });
    setShowForm(true);
  };

  const startEdit = (l) => {
    setEditing(l.id);
    setForm({
      entity_id: l.entity_id,
      property_id: l.property_id || "",
      project_id: l.project_id || "",
      tenant_name: l.tenant_name || "",
      tenant_siret: l.tenant_siret || "",
      tenant_contact: l.tenant_contact || "",
      property_label: l.property_label || "",
      start_date: l.start_date || "",
      end_date: l.end_date || "",
      duration_months: l.duration_months || 36,
      monthly_rent_ht: l.monthly_rent_ht || 0,
      monthly_charges: l.monthly_charges || 0,
      tva_rate: l.tva_rate ?? 20,
      taxe_fonciere_annual: l.taxe_fonciere_annual || 0,
      taxe_fonciere_refacturee: !!l.taxe_fonciere_refacturee,
      deposit: l.deposit || 0,
      indexation_index: l.indexation_index || "ILC",
      indexation_rate_pct: l.indexation_rate_pct || 0,
      initial_rent_ht: l.initial_rent_ht ?? "",
      indexation_reference_index: l.indexation_reference_index ?? "",
      indexation_reference_quarter: l.indexation_reference_quarter || "",
      indexation_next_review_date: l.indexation_next_review_date || "",
      status: l.status || "actif",
      notes: l.notes || "",
      payment_day: l.payment_day ?? 1,
      current_month_collected: !!l.current_month_collected,
      current_month_collected_at: l.current_month_collected_at || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      property_id: form.property_id || null,
      project_id: form.project_id || null,
      duration_months: parseInt(form.duration_months) || null,
      monthly_rent_ht: parseFloat(form.monthly_rent_ht) || 0,
      monthly_charges: parseFloat(form.monthly_charges) || 0,
      tva_rate: parseFloat(form.tva_rate) || 0,
      taxe_fonciere_annual: parseFloat(form.taxe_fonciere_annual) || 0,
      deposit: parseFloat(form.deposit) || 0,
      indexation_rate_pct: parseFloat(form.indexation_rate_pct) || 0,
      initial_rent_ht: form.initial_rent_ht === "" ? null : parseFloat(form.initial_rent_ht),
      indexation_reference_index: form.indexation_reference_index === "" ? null : parseFloat(form.indexation_reference_index),
      indexation_reference_quarter: form.indexation_reference_quarter || null,
      indexation_next_review_date: form.indexation_next_review_date || null,
      end_date: form.end_date || null,
      payment_day: form.payment_day === "" ? null : (parseInt(form.payment_day) || null),
      current_month_collected: !!form.current_month_collected,
      current_month_collected_at: form.current_month_collected_at || null,
    };
    try {
      if (editing) {
        await api.put(`/backoffice/leases/${editing}`, payload);
        toast.success("Bail mis à jour");
      } else {
        await api.post("/backoffice/leases", payload);
        toast.success("Bail créé");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bail ? Les KPIs seront recalculés.")) return;
    await api.delete(`/backoffice/leases/${id}`);
    toast.success("Supprimé");
    load();
  };

  const toggleCollected = async (l) => {
    const currentYm = new Date().toISOString().slice(0, 7); // YYYY-MM
    const isCollected = !!l.current_month_collected && l.current_month_collected_at === currentYm;
    try {
      await api.put(`/backoffice/leases/${l.id}`, {
        current_month_collected: !isCollected,
        current_month_collected_at: !isCollected ? currentYm : null,
      });
      toast.success(!isCollected ? "Loyer marqué encaissé ce mois" : "Encaissement annulé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // === VACANCE & IMPAYÉS ===
  const [vacancyLease, setVacancyLease] = useState(null); // lease en cours d'édition dans le modal
  const [vacancyDraft, setVacancyDraft] = useState({ start: "", end: "", reason: "vacant", note: "" });

  const currentYmStr = () => new Date().toISOString().slice(0, 7);

  const quickMarkUnpaid = async (l) => {
    const ym = currentYmStr();
    const list = Array.isArray(l.vacancy_periods) ? [...l.vacancy_periods] : [];
    const existing = list.find((p) => p.start === ym && p.end === ym && p.reason === "unpaid");
    let updated;
    if (existing) {
      updated = list.filter((p) => p !== existing);
    } else {
      updated = [...list, { id: `unpaid-${ym}-${Date.now()}`, start: ym, end: ym, reason: "unpaid", note: "Impayé (rapide)" }];
    }
    try {
      await api.put(`/backoffice/leases/${l.id}`, { vacancy_periods: updated });
      toast.success(existing ? "Impayé retiré" : `Impayé marqué ${ym}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const addVacancyPeriod = async () => {
    if (!vacancyLease || !vacancyDraft.start) return toast.error("Mois de début requis");
    const list = Array.isArray(vacancyLease.vacancy_periods) ? [...vacancyLease.vacancy_periods] : [];
    list.push({
      id: `v-${Date.now()}`,
      start: vacancyDraft.start,
      end: vacancyDraft.end || null,
      reason: vacancyDraft.reason,
      note: vacancyDraft.note || null,
    });
    try {
      await api.put(`/backoffice/leases/${vacancyLease.id}`, { vacancy_periods: list });
      toast.success("Période ajoutée");
      setVacancyDraft({ start: "", end: "", reason: "vacant", note: "" });
      // Recharger le bail
      const fresh = await api.get(`/backoffice/leases/${vacancyLease.id}`);
      setVacancyLease(fresh.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const removeVacancyPeriod = async (periodId) => {
    if (!vacancyLease) return;
    const list = (vacancyLease.vacancy_periods || []).filter((p) => p.id !== periodId);
    try {
      await api.put(`/backoffice/leases/${vacancyLease.id}`, { vacancy_periods: list });
      toast.success("Période supprimée");
      const fresh = await api.get(`/backoffice/leases/${vacancyLease.id}`);
      setVacancyLease(fresh.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // ===== Import depuis /admin/baux (bridge legacy) =====
  const openImport = async () => {
    try {
      const r = await api.get("/backoffice/leases-import/candidates");
      const list = r.data?.leases || [];
      setImportCandidates(list);
      // pré-sélectionner tous les non-importés
      const sel = {};
      list.forEach((l) => { if (!l.already_imported) sel[l.admin_lease_id] = true; });
      setImportSelection(sel);
      setImportEntityId(entities[0]?.id || "");
      setShowImport(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de charger la liste des baux");
    }
  };

  const runImport = async () => {
    if (!importEntityId) return toast.error("Choisis une entité de destination");
    const ids = Object.entries(importSelection).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error("Aucun bail sélectionné");
    setImportBusy(true);
    try {
      const r = await api.post("/backoffice/leases-import/execute", {
        entity_id: importEntityId,
        admin_lease_ids: ids,
      });
      const { imported, skipped } = r.data || {};
      toast.success(`${imported} bail(s) importé(s)${skipped ? `, ${skipped} déjà présent(s)` : ""}`);
      setShowImport(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImportBusy(false);
    }
  };

  // ===== Modal de révision d'indexation =====
  const [revisingLease, setRevisingLease] = useState(null);
  const [revForm, setRevForm] = useState({ new_index: "", new_quarter: "", effective_date: "", notes: "" });
  const [revPreview, setRevPreview] = useState(null);

  const openRevise = (l) => {
    setRevisingLease(l);
    setRevForm({
      new_index: "",
      new_quarter: "",
      effective_date: l.indexation_next_review_date || new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setRevPreview(null);
  };

  const previewRevision = async () => {
    if (!revisingLease || !revForm.new_index) return;
    try {
      const r = await api.post(`/backoffice/leases/${revisingLease.id}/preview-revision`, {
        new_index: parseFloat(revForm.new_index),
        new_quarter: revForm.new_quarter || null,
        effective_date: revForm.effective_date,
        notes: revForm.notes || null,
      });
      setRevPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur preview");
    }
  };

  const applyRevision = async () => {
    if (!revisingLease || !revForm.new_index) return;
    try {
      await api.post(`/backoffice/leases/${revisingLease.id}/revise`, {
        new_index: parseFloat(revForm.new_index),
        new_quarter: revForm.new_quarter || null,
        effective_date: revForm.effective_date,
        notes: revForm.notes || null,
      });
      toast.success("Révision appliquée. Loyer et prochaine échéance mis à jour.");
      setRevisingLease(null);
      setRevPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const rollbackRevision = async (leaseId, revisionId) => {
    if (!window.confirm("Annuler cette révision ? Le loyer sera restauré.")) return;
    await api.delete(`/backoffice/leases/${leaseId}/revisions/${revisionId}`);
    toast.success("Révision annulée");
    // Refresh the modal state
    if (revisingLease?.id === leaseId) {
      const upd = (await api.get(`/backoffice/leases/${leaseId}`)).data;
      setRevisingLease(upd);
    }
    load();
  };

  // Totals
  const activeItems = items.filter((i) => i.status === "actif");
  const totalRent = activeItems.reduce((s, i) => s + (i.kpi?.loyer_ht_mensuel || 0), 0);
  const totalCharges = activeItems.reduce((s, i) => s + (i.kpi?.charges_refacturees_mensuel || 0), 0);
  const totalTF = activeItems.reduce((s, i) => s + (i.kpi?.taxe_fonciere_mensuel || 0), 0);
  const totalCF = activeItems.reduce((s, i) => s + (i.kpi?.cash_flow_mensuel || 0), 0);

  // ==== TAUX D'OCCUPATION 12M rolling ====
  // Pour chaque bail actif × chacun des 12 derniers mois : compte les mois où le bail est en vacance/impayé.
  // occupation_pct = (bail-mois total - bail-mois en vacance) / bail-mois total.
  const now = new Date();
  const rolling12 = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    rolling12.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  const monthCovered = (periods, ym) => {
    if (!Array.isArray(periods)) return null;
    for (const p of periods) {
      if (!p?.start) continue;
      if (ym < p.start) continue;
      if (p.end && ym > p.end) continue;
      return p; // covered
    }
    return null;
  };
  let totalUnitMonths = 0;
  let vacantMonths = 0;
  let lostRent12M = 0;
  activeItems.forEach((lease) => {
    rolling12.forEach((ym) => {
      totalUnitMonths += 1;
      const p = monthCovered(lease.vacancy_periods, ym);
      if (p) {
        vacantMonths += 1;
        // Loss = rent + charges + prorated TF si refacturée
        const rent = lease.monthly_rent_ht || 0;
        const charges = lease.monthly_charges || 0;
        const tfMonthly = lease.taxe_fonciere_refacturee ? ((lease.taxe_fonciere_annual || 0) / 12) : 0;
        lostRent12M += rent + charges + tfMonthly;
      }
    });
  });
  const occupancyPct = totalUnitMonths > 0 ? ((totalUnitMonths - vacantMonths) / totalUnitMonths) * 100 : 100;

  return (
    <BackofficeLayout
      title="Baux locatifs"
      subtitle="Locataires par bâtiment / bien. Loyer, charges, taxe foncière → alimentent automatiquement le cash-flow groupe."
      actions={
        <div className="flex items-center gap-2">
          <button onClick={openImport}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition"
                  data-testid="bof-lease-import-btn">
            <Download className="w-3.5 h-3.5" /> Importer depuis /admin/baux
          </button>
          <Btn onClick={openNew} data-testid="bof-lease-new-btn"><Plus className="w-3.5 h-3.5" /> Nouveau bail</Btn>
        </div>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {/* KPIs baux actifs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="bof-leases-kpis">
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Baux actifs</div>
          <div className="text-2xl font-black text-white mt-1 flex items-center gap-2"><Users className="w-5 h-5 text-amber-400" />{activeItems.length}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Loyer HT / mois</div>
          <div className="text-xl font-black text-emerald-300 mt-1">{fmtEUR(totalRent)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Charges refact. / mois</div>
          <div className="text-xl font-black text-sky-300 mt-1">{fmtEUR(totalCharges)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cash-flow / mois</div>
          <div className="text-xl font-black text-amber-300 mt-1">{fmtEUR(totalCF)}</div></Card>
        <Card data-testid="bof-lease-kpi-occupancy">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Taux d&apos;occupation 12M</div>
          <div className={`text-xl font-black mt-1 ${occupancyPct >= 95 ? "text-emerald-300" : occupancyPct >= 85 ? "text-amber-300" : "text-red-300"}`}>
            {occupancyPct.toFixed(1)} %
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {vacantMonths}/{totalUnitMonths} bail-mois en vacance
          </div>
        </Card>
        <Card data-testid="bof-lease-kpi-lost">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Loyer perdu 12M</div>
          <div className={`text-xl font-black mt-1 ${lostRent12M > 0 ? "text-red-300" : "text-slate-500"}`}>
            {lostRent12M > 0 ? "−" : ""}{fmtEUR(lostRent12M)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">vacances + impayés</div>
        </Card>
      </div>

      {/* Filter */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Filtrer statut:</span>
        {["", "actif", "en_negociation", "expire", "rompu"].map((s) => (
          <button key={s || "all"} onClick={() => setFilterStatus(s)}
                  className={`text-xs px-2.5 py-1 rounded border ${filterStatus === s ? "bg-amber-400 text-slate-900 border-amber-500" : "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800"}`}
                  data-testid={`bof-lease-filter-${s || "all"}`}>
            {s ? STATUS_LABEL[s] : "Tous"}
          </button>
        ))}
      </div>

      {showForm && (
        <Card className="mb-6" data-testid="bof-lease-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Section 1: Entité + bien + projet */}
            <F label="Entité propriétaire">
              <select required value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input" data-testid="bof-lease-entity">
                <option value="">— Choisir —</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </F>
            <F label="Bien du site public (optionnel)">
              <select value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} className="input">
                <option value="">— Aucun —</option>
                {existingProps.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </F>
            <F label="Projet (optionnel)">
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="input">
                <option value="">— Aucun —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Libellé du bien" span={3}>
              <input value={form.property_label} onChange={(e) => setForm({ ...form, property_label: e.target.value })}
                     placeholder="Site 1 - Local A - 450 m²" className="input" data-testid="bof-lease-property-label" />
            </F>

            {/* Section 2: Locataire */}
            <F label="Nom du locataire">
              <input required value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
                     placeholder="Logistique Express SARL" className="input" data-testid="bof-lease-tenant" />
            </F>
            <F label="SIRET">
              <input value={form.tenant_siret} onChange={(e) => setForm({ ...form, tenant_siret: e.target.value })} className="input" />
            </F>
            <F label="Contact (email / tél)">
              <input value={form.tenant_contact} onChange={(e) => setForm({ ...form, tenant_contact: e.target.value })} className="input" />
            </F>

            {/* Section 3: Dates */}
            <F label="Date début">
              <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input" />
            </F>
            <F label="Date fin">
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input" />
            </F>
            <F label="Durée (mois)">
              <input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} className="input" placeholder="36 (3-6-9)" />
            </F>

            {/* Section 4: Montants */}
            <F label="Loyer HT / mois (€)">
              <input type="number" step="0.01" required value={form.monthly_rent_ht} onChange={(e) => setForm({ ...form, monthly_rent_ht: e.target.value })} className="input" data-testid="bof-lease-rent" />
            </F>
            <F label="Charges refacturées / mois (€)">
              <input type="number" step="0.01" value={form.monthly_charges} onChange={(e) => setForm({ ...form, monthly_charges: e.target.value })} className="input" />
            </F>
            <F label="TVA (%)">
              <input type="number" step="0.1" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} className="input" />
            </F>
            <F label="Taxe foncière annuelle (€)">
              <input type="number" step="0.01" value={form.taxe_fonciere_annual} onChange={(e) => setForm({ ...form, taxe_fonciere_annual: e.target.value })} className="input" data-testid="bof-lease-tf" />
            </F>
            <F label="Taxe foncière refacturée ?">
              <select value={form.taxe_fonciere_refacturee ? "yes" : "no"} onChange={(e) => setForm({ ...form, taxe_fonciere_refacturee: e.target.value === "yes" })} className="input">
                <option value="no">Non — supportée par le propriétaire</option>
                <option value="yes">Oui — refacturée au locataire</option>
              </select>
            </F>
            <F label="Dépôt de garantie (€)">
              <input type="number" step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className="input" />
            </F>

            {/* Section 5: Indexation + statut */}
            <F label="Indice d'indexation">
              <select value={form.indexation_index} onChange={(e) => setForm({ ...form, indexation_index: e.target.value })} className="input">
                <option value="ILC">ILC (commerces)</option>
                <option value="ILAT">ILAT (bureaux/activités tertiaires)</option>
                <option value="IRL">IRL (logement)</option>
                <option value="AUTRE">Autre</option>
                <option value="NONE">Aucune</option>
              </select>
            </F>
            <F label="Loyer initial HT (M0) — €">
              <input type="number" step="0.01" value={form.initial_rent_ht}
                     onChange={(e) => setForm({ ...form, initial_rent_ht: e.target.value })}
                     placeholder="Ex: 6500 (défaut = loyer actuel)" className="input" data-testid="bof-lease-initial-rent" />
            </F>
            <F label="Indice de référence (INSEE)">
              <input type="number" step="0.01" value={form.indexation_reference_index}
                     onChange={(e) => setForm({ ...form, indexation_reference_index: e.target.value })}
                     placeholder="Ex: 126.13" className="input" data-testid="bof-lease-ref-index" />
            </F>
            <F label="Trimestre de référence">
              <select value={form.indexation_reference_quarter}
                      onChange={(e) => setForm({ ...form, indexation_reference_quarter: e.target.value })}
                      className="input" data-testid="bof-lease-ref-quarter">
                <option value="">— Choisir —</option>
                {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </F>
            <F label="Prochaine révision (date anniversaire)">
              <input type="date" value={form.indexation_next_review_date}
                     onChange={(e) => setForm({ ...form, indexation_next_review_date: e.target.value })}
                     className="input" data-testid="bof-lease-next-review" />
            </F>
            <F label="Taux fallback annuel (%) — si pas d'indice INSEE">
              <input type="number" step="0.01" value={form.indexation_rate_pct} onChange={(e) => setForm({ ...form, indexation_rate_pct: e.target.value })} className="input" />
            </F>
            <F label="Statut">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input" data-testid="bof-lease-status">
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            <F label="Jour d'encaissement du loyer (1-31)">
              <input type="number" min="1" max="31" value={form.payment_day}
                     onChange={(e) => setForm({ ...form, payment_day: e.target.value })}
                     placeholder="Ex: 3 (loyer perçu le 3 de chaque mois)" className="input"
                     data-testid="bof-lease-payment-day" />
              <div className="text-[10px] text-slate-500 mt-1">
                Utilisé pour savoir si le loyer est déjà encaissé sur le mois courant (par rapport à la date de solde du compte).
              </div>
            </F>
            <F label="Notes" span={3}>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
            </F>

            <div className="md:col-span-3 flex gap-2">
              <Btn type="submit" data-testid="bof-lease-save">{editing ? "Enregistrer" : "Créer le bail"}</Btn>
              <Btn type="button" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Leases list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bof-leases-list">
        {items.length === 0 && (
          <div className="lg:col-span-2 text-center text-slate-500 py-12 border border-dashed border-slate-800 rounded-lg">
            Aucun bail. Créez votre premier bail locatif pour alimenter automatiquement le cash-flow groupe.
          </div>
        )}
        {items.map((l) => (
          <Card key={l.id} data-testid={`bof-lease-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <FileSignature className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase ${STATUS_TONE[l.status] || STATUS_TONE.actif}`}>
                      {STATUS_LABEL[l.status] || l.status}
                    </span>
                    {l.indexation_index && l.indexation_index !== "NONE" && (
                      <span className="text-[10px] px-2 py-0.5 rounded border font-semibold uppercase bg-slate-500/10 text-slate-400 border-slate-500/30">
                        {l.indexation_index} · +{l.indexation_rate_pct}%/an
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-white text-base">{l.tenant_name}</div>
                  {l.tenant_siret && <div className="text-xs text-slate-500">SIRET {l.tenant_siret}</div>}
                  {l.property_label && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Building className="w-3 h-3" /> {l.property_label}</div>}
                  <div className="text-xs text-slate-500 mt-0.5">{l.entity_name}{l.project_name ? ` · Projet ${l.project_name}` : ""}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap">
                <Btn variant="ghost" onClick={() => openRevise(l)} data-testid={`bof-lease-revise-${l.id}`}
                     className="text-amber-300 hover:bg-amber-500/10">
                  <TrendingUp className="w-3.5 h-3.5" /> Réviser
                </Btn>
                <Btn variant="ghost" onClick={() => startEdit(l)} data-testid={`bof-lease-edit-${l.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Btn>
                <Btn variant="danger" onClick={() => remove(l.id)} data-testid={`bof-lease-delete-${l.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Btn>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <MiniRow label="Loyer HT / mois" value={fmtEUR(l.monthly_rent_ht)} tone="emerald" />
              <MiniRow label="Charges refact. / mois" value={fmtEUR(l.monthly_charges)} tone="sky" />
              <MiniRow label="Taxe foncière / an" value={fmtEUR(l.taxe_fonciere_annual)}
                       sub={l.taxe_fonciere_refacturee ? "Refacturée" : "Propriétaire"} tone="red" />
              <MiniRow label="Dépôt de garantie" value={fmtEUR(l.deposit)} tone="slate" />
            </div>

            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Revenu mensuel total</div>
                  <div className="text-lg font-black text-emerald-300">{fmtEUR(l.kpi?.revenu_mensuel_total)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cash-flow / mois</div>
                  <div className={`text-lg font-black ${(l.kpi?.cash_flow_mensuel || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {fmtEUR(l.kpi?.cash_flow_mensuel)}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Début {l.start_date}{l.end_date ? ` → ${l.end_date}` : ""}</span>
                <span>Revenu annuel {fmtEUR(l.kpi?.revenu_annuel_total)}</span>
                {l.payment_day && <span className="text-slate-400">Perçu le {l.payment_day} de chaque mois</span>}
                {l.indexation_reference_index && (
                  <span className="text-slate-400">Indice ref: {l.indexation_reference_index}{l.indexation_reference_quarter ? ` (${l.indexation_reference_quarter})` : ""}</span>
                )}
                {l.indexation_next_review_date && (
                  <span className="text-amber-400">📅 Prochaine révision: {l.indexation_next_review_date}</span>
                )}
              </div>

              {/* Toggle "loyer encaissé ce mois" + Vacance/impayé */}
              {(() => {
                const currentYm = new Date().toISOString().slice(0, 7);
                const isCollected = !!l.current_month_collected && l.current_month_collected_at === currentYm;
                const vps = Array.isArray(l.vacancy_periods) ? l.vacancy_periods : [];
                const currentlyUnpaid = vps.some((p) => p.start <= currentYm && (!p.end || p.end >= currentYm) && p.reason === "unpaid");
                const currentlyVacant = vps.some((p) => p.start <= currentYm && (!p.end || p.end >= currentYm) && p.reason !== "unpaid");
                return (
                  <div className="mt-3 space-y-2">
                    <button onClick={() => toggleCollected(l)}
                            className={`w-full text-xs font-semibold uppercase tracking-widest px-3 py-2 rounded border transition
                              ${isCollected
                                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                                : "bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800"}`}
                            data-testid={`bof-lease-toggle-collected-${l.id}`}>
                      {isCollected
                        ? `✓ Loyer déjà encaissé ce mois (${currentYm}) — clic pour annuler`
                        : `Marquer le loyer comme encaissé ce mois (${currentYm})`}
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => quickMarkUnpaid(l)}
                              className={`text-xs font-semibold uppercase tracking-widest px-3 py-2 rounded border transition
                                ${currentlyUnpaid
                                  ? "bg-red-500/15 border-red-500/40 text-red-300 hover:bg-red-500/25"
                                  : "bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800"}`}
                              data-testid={`bof-lease-toggle-unpaid-${l.id}`}>
                        {currentlyUnpaid ? `✗ Impayé ${currentYm}` : `Marquer impayé ${currentYm}`}
                      </button>
                      <button onClick={() => setVacancyLease(l)}
                              className={`text-xs font-semibold uppercase tracking-widest px-3 py-2 rounded border transition
                                ${vps.length > 0
                                  ? "bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
                                  : "bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800"}`}
                              data-testid={`bof-lease-vacancy-manage-${l.id}`}>
                        {vps.length > 0
                          ? `⚠ Vacance/Impayés (${vps.length})`
                          : `Ajouter période vacance`}
                      </button>
                    </div>
                    {(currentlyUnpaid || currentlyVacant) && (
                      <div className={`text-[10px] uppercase tracking-widest font-bold text-center py-1 rounded ${currentlyUnpaid ? "bg-red-500/10 text-red-300" : "bg-amber-500/10 text-amber-300"}`}>
                        {currentlyUnpaid ? `⚠ Loyer non perçu ce mois` : `⚠ Local vacant ce mois`}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Historique des révisions */}
              {(l.revisions || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800/60" data-testid={`bof-lease-revisions-${l.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
                    <History className="w-3 h-3" /> Historique des révisions ({l.revisions.length})
                  </div>
                  <div className="space-y-1">
                    {l.revisions.slice(-3).reverse().map((rev) => (
                      <div key={rev.id} className="flex items-center justify-between text-xs bg-slate-900/60 border border-slate-800 rounded px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-slate-400">{rev.effective_date}</span>
                          <span className="text-slate-500">•</span>
                          <span className="text-slate-300">{fmtEUR(rev.previous_rent)} → <span className="font-bold text-white">{fmtEUR(rev.new_rent)}</span></span>
                          <span className={rev.variation_pct >= 0 ? "text-emerald-300 text-[10px]" : "text-red-300 text-[10px]"}>
                            ({rev.variation_pct >= 0 ? "+" : ""}{rev.variation_pct}%)
                          </span>
                          {rev.new_quarter && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">{rev.new_quarter}: {rev.new_index}</span>}
                        </div>
                        <button onClick={() => rollbackRevision(l.id, rev.id)}
                                className="text-red-400 hover:text-red-300 text-[10px] font-semibold"
                                data-testid={`bof-lease-rollback-${rev.id}`}>
                          Annuler
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* ================ MODAL RÉVISION ================ */}
      {revisingLease && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={(e) => e.target === e.currentTarget && setRevisingLease(null)}
             data-testid="bof-revise-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-800 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">Indexation INSEE — Règle proportionnelle</div>
                <h2 className="text-xl font-black text-white">Réviser le loyer</h2>
                <div className="text-sm text-slate-400 mt-1">{revisingLease.tenant_name} · {revisingLease.property_label || "—"}</div>
              </div>
              <button onClick={() => setRevisingLease(null)} className="text-slate-500 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-800/60 border border-slate-700 rounded p-4 text-sm space-y-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-400">Loyer initial (M0):</span> <span className="font-mono font-bold text-white">{fmtEUR(revisingLease.initial_rent_ht || revisingLease.monthly_rent_ht)}</span></div>
                  <div><span className="text-slate-400">Indice de référence:</span> <span className="font-mono font-bold text-white">{revisingLease.indexation_reference_index || "—"} {revisingLease.indexation_reference_quarter ? `(${revisingLease.indexation_reference_quarter})` : ""}</span></div>
                  <div><span className="text-slate-400">Loyer actuel:</span> <span className="font-mono font-bold text-emerald-300">{fmtEUR(revisingLease.monthly_rent_ht)}</span></div>
                  <div><span className="text-slate-400">Indice:</span> <span className="font-mono text-white">{revisingLease.indexation_index}</span></div>
                </div>
              </div>

              {!revisingLease.indexation_reference_index && (
                <div className="bg-red-500/10 border border-red-800 text-red-200 rounded p-3 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Renseignez d&apos;abord l&apos;<b>indice de référence</b> et le <b>trimestre de référence</b> dans les paramètres du bail (bouton Modifier).</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Nouvel indice INSEE</label>
                  <input type="number" step="0.01" value={revForm.new_index}
                         onChange={(e) => { setRevForm({ ...revForm, new_index: e.target.value }); setRevPreview(null); }}
                         placeholder="Ex: 138.42" className="input" data-testid="bof-revise-new-index" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Trimestre du nouvel indice</label>
                  <select value={revForm.new_quarter}
                          onChange={(e) => setRevForm({ ...revForm, new_quarter: e.target.value })}
                          className="input" data-testid="bof-revise-new-quarter">
                    <option value="">— Choisir —</option>
                    {QUARTERS.slice().reverse().map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Date d&apos;effet</label>
                  <input type="date" value={revForm.effective_date}
                         onChange={(e) => setRevForm({ ...revForm, effective_date: e.target.value })}
                         className="input" data-testid="bof-revise-effective-date" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Notes</label>
                  <input value={revForm.notes}
                         onChange={(e) => setRevForm({ ...revForm, notes: e.target.value })}
                         placeholder="Ex: Révision annuelle contractuelle" className="input" />
                </div>
              </div>

              <div className="flex gap-2">
                <Btn onClick={previewRevision}
                     disabled={!revForm.new_index || !revisingLease.indexation_reference_index}
                     data-testid="bof-revise-preview-btn">
                  Prévisualiser
                </Btn>
                <Btn variant="ghost" onClick={() => setRevisingLease(null)}>Annuler</Btn>
              </div>

              {revPreview && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/40 rounded-lg" data-testid="bof-revise-preview">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-3">Aperçu du calcul INSEE</div>
                  <div className="text-xs text-slate-300 mb-3 font-mono">
                    Nouveau loyer = {fmtEUR(revPreview.loyer_initial)} × {revPreview.nouvel_indice} ÷ {revPreview.indice_reference}
                    <span className="text-amber-300 font-bold ml-2">= {fmtEUR(revPreview.nouveau_loyer)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ancien loyer</div>
                      <div className="text-lg font-black text-slate-300">{fmtEUR(revPreview.loyer_actuel)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Nouveau loyer</div>
                      <div className="text-lg font-black text-emerald-300">{fmtEUR(revPreview.nouveau_loyer)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Variation</div>
                      <div className={`text-lg font-black ${revPreview.variation_eur >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {revPreview.variation_eur >= 0 ? "+" : ""}{fmtEUR(revPreview.variation_eur)}
                      </div>
                      <div className="text-[10px] text-slate-500">{revPreview.variation_pct >= 0 ? "+" : ""}{revPreview.variation_pct}% / {revPreview.variation_vs_initial_pct >= 0 ? "+" : ""}{revPreview.variation_vs_initial_pct}% vs M0</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-amber-500/20 text-sm">
                    <span className="text-slate-400">Impact annuel :</span>{" "}
                    <span className={`font-bold ${revPreview.impact_annuel >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {revPreview.impact_annuel >= 0 ? "+" : ""}{fmtEUR(revPreview.impact_annuel)}
                    </span>
                  </div>
                  <Btn onClick={applyRevision} className="mt-4 w-full justify-center"
                       data-testid="bof-revise-apply-btn">
                    <Save className="w-3.5 h-3.5" /> Appliquer la révision
                  </Btn>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Import depuis /admin/baux */}
      {showImport && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10"
             onClick={(e) => { if (e.target === e.currentTarget) setShowImport(false); }}
             data-testid="bof-lease-import-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-3xl shadow-2xl">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <div className="text-sm font-black uppercase tracking-widest text-white">Importer les baux existants</div>
                <div className="text-xs text-slate-400 mt-1">Baux gérés depuis <code>/admin/baux</code> — les baux déjà migrés ne sont pas ré-importés.</div>
              </div>
              <button onClick={() => setShowImport(false)} className="text-slate-500 hover:text-white text-lg leading-none">×</button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Entité de destination</label>
                <select value={importEntityId} onChange={(e) => setImportEntityId(e.target.value)}
                        className="input" data-testid="bof-lease-import-entity">
                  <option value="">— Choisir une entité —</option>
                  {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>

              <div className="border border-slate-800 rounded overflow-hidden">
                {importCandidates.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-500">Aucun bail trouvé dans <code>/admin/baux</code>.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/60 text-slate-400 text-[10px] uppercase tracking-widest">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left">Locataire</th>
                        <th className="px-3 py-2 text-left">Local</th>
                        <th className="px-3 py-2 text-right">Loyer / mois</th>
                        <th className="px-3 py-2 text-left">Début</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importCandidates.map((L) => {
                        const disabled = L.already_imported;
                        return (
                          <tr key={L.admin_lease_id}
                              className={`border-t border-slate-800 ${disabled ? "opacity-50" : "hover:bg-slate-800/40"}`}
                              data-testid={`bof-lease-import-row-${L.admin_lease_id}`}>
                            <td className="px-3 py-2">
                              <input type="checkbox"
                                     disabled={disabled}
                                     checked={!!importSelection[L.admin_lease_id]}
                                     onChange={(e) => setImportSelection({ ...importSelection, [L.admin_lease_id]: e.target.checked })}
                                     data-testid={`bof-lease-import-check-${L.admin_lease_id}`} />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-bold text-white">{L.tenant_name}</div>
                              {L.tenant_email && <div className="text-[10px] text-slate-500">{L.tenant_email}</div>}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-400 max-w-xs truncate">{L.property_label}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-300">{fmtEUR(L.rent_monthly)}</td>
                            <td className="px-3 py-2 text-xs text-slate-400">{L.start_date}</td>
                            <td className="px-3 py-2 text-xs">
                              {L.already_imported
                                ? <span className="text-slate-500 italic">déjà importé</span>
                                : <span className="text-slate-300">{L.status || "—"}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-800/40 border border-slate-800 rounded p-3">
                Les baux seront créés dans <code>bof_leases</code> avec le mapping suivant : loyer HT = <code>rent_monthly</code>, charges = <code>charges_monthly</code>,
                TVA = 20% par défaut, indexation = <b>NONE</b> (à paramétrer ensuite). Le champ <code>admin_lease_id</code> conserve la trace de l&apos;origine
                — si le bail est ré-importé, il est ignoré (pas de doublon).
              </div>
            </div>

            <div className="p-5 border-t border-slate-700 flex items-center justify-end gap-2">
              <button onClick={() => setShowImport(false)}
                      className="px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white">
                Annuler
              </button>
              <button onClick={runImport} disabled={importBusy}
                      className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-sky-500/20 border border-sky-500/40 text-sky-300 hover:bg-sky-500/30 rounded disabled:opacity-50"
                      data-testid="bof-lease-import-submit">
                {importBusy ? "Import en cours…" : `Importer ${Object.values(importSelection).filter(Boolean).length} bail(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>

      {/* ==== Modal Vacance / Impayés ==== */}
      {vacancyLease && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10"
             onClick={(e) => { if (e.target === e.currentTarget) setVacancyLease(null); }}
             data-testid="bof-vacancy-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-2xl shadow-2xl">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <div className="text-sm font-black uppercase tracking-widest text-white">Vacance &amp; Impayés</div>
                <div className="text-xs text-slate-400 mt-1">
                  {vacancyLease.tenant_name} · {vacancyLease.property_label || "—"} · Loyer {fmtEUR(vacancyLease.monthly_rent_ht)} / mois
                </div>
              </div>
              <button onClick={() => setVacancyLease(null)} className="text-slate-500 hover:text-white text-lg leading-none">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-800/40 border border-slate-800 rounded p-3">
                Ajoute une période où le loyer <b>n&apos;est pas perçu</b> — elle sera automatiquement retirée du calcul de trésorerie pour les mois concernés.
                Laisse le champ <b>fin</b> vide pour une période ouverte (jusqu&apos;à re-location).
              </div>

              {/* Table existante */}
              <div className="border border-slate-800 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800/60 text-slate-500 text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="px-3 py-2 text-left">Du</th>
                      <th className="px-3 py-2 text-left">Au</th>
                      <th className="px-3 py-2 text-left">Raison</th>
                      <th className="px-3 py-2 text-left">Note</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(vacancyLease.vacancy_periods || []).length === 0 ? (
                      <tr><td colSpan="5" className="px-3 py-6 text-center text-slate-500 italic">Aucune période enregistrée</td></tr>
                    ) : (vacancyLease.vacancy_periods.map((p) => (
                      <tr key={p.id} className="border-t border-slate-800" data-testid={`bof-vacancy-row-${p.id}`}>
                        <td className="px-3 py-2 font-mono">{p.start}</td>
                        <td className="px-3 py-2 font-mono">{p.end || <span className="text-slate-500 italic">ouverte</span>}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                            p.reason === "unpaid" ? "bg-red-500/15 border-red-500/40 text-red-300"
                            : p.reason === "free_month" ? "bg-sky-500/15 border-sky-500/40 text-sky-300"
                            : p.reason === "carence" ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
                            : "bg-amber-500/15 border-amber-500/40 text-amber-300"
                          }`}>
                            {p.reason === "unpaid" ? "Impayé" : p.reason === "free_month" ? "Franchise" : p.reason === "carence" ? "Carence" : "Vacant"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-400 text-[11px]">{p.note || "—"}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeVacancyPeriod(p.id)} className="text-red-400 hover:text-red-300" data-testid={`bof-vacancy-remove-${p.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>

              {/* Formulaire ajout */}
              <div className="p-4 border border-slate-800 rounded space-y-3 bg-slate-800/20">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ajouter une période</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Du (mois)</span>
                    <input type="month" value={vacancyDraft.start} onChange={(e) => setVacancyDraft({ ...vacancyDraft, start: e.target.value })} className="input" data-testid="bof-vacancy-start" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Au (mois, optionnel)</span>
                    <input type="month" value={vacancyDraft.end} onChange={(e) => setVacancyDraft({ ...vacancyDraft, end: e.target.value })} className="input" data-testid="bof-vacancy-end" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Raison</span>
                    <select value={vacancyDraft.reason} onChange={(e) => setVacancyDraft({ ...vacancyDraft, reason: e.target.value })} className="input" data-testid="bof-vacancy-reason">
                      <option value="vacant">Local vacant (non reloué)</option>
                      <option value="unpaid">Impayé locataire</option>
                      <option value="free_month">Franchise commerciale</option>
                      <option value="carence">Carence loyer</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Note</span>
                    <input type="text" value={vacancyDraft.note} onChange={(e) => setVacancyDraft({ ...vacancyDraft, note: e.target.value })} placeholder="Ex : locataire en RJ" className="input" data-testid="bof-vacancy-note" />
                  </label>
                </div>
                <button onClick={addVacancyPeriod}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-bold bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 rounded"
                        data-testid="bof-vacancy-add-btn">
                  <Plus className="w-3.5 h-3.5" /> Ajouter la période
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </BackofficeLayout>
  );
}

const F = ({ label, children, span = 1 }) => (
  <label className={`block ${span === 3 ? "md:col-span-3" : ""}`}>
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);

const MiniRow = ({ label, value, sub, tone = "slate" }) => {
  const tones = { emerald: "text-emerald-300", sky: "text-sky-300", red: "text-red-300", slate: "text-slate-200" };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};
